import React, { useState } from 'react';
import { MdShare } from 'react-icons/md';
import Button from 'components/atoms/Button/Button';
import Card from 'components/atoms/Card/Card';
import Heading from 'components/atoms/Heading/Heading';
import Txt from 'components/atoms/Txt/Txt';
import SharePopover from 'components/molecules/SocialSharePopover/SocialSharePopover';
import classes from './OfferBanner.module.scss';
import clsx from 'clsx';
import { mobileConfigFromTags, TITLE_SUFFIX_BY_TYPE, type OfferType } from 'utils/offers';

export type Props = {
  offerType?: OfferType;
  title: string;
  bannerImageSrc?: string;
  description?: string;
  tags?: string[];
  className?: string;
  defaultImg?: string;
  showShareOptions?: boolean;
  shareUrl?: string;
};

const OfferBanner = ({
  offerType = 'cashback',
  title,
  bannerImageSrc,
  description,
  tags,
  className,
  defaultImg,
  showShareOptions = false,
  shareUrl,
}: Props) => {
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const finalBannerImageSrc = bannerImageSrc || defaultImg || '/coin.svg';
  const isDefaultIcon = !bannerImageSrc;
  const offerTags = tags ?? [];

  const mobileConfig = mobileConfigFromTags(offerTags);
  const mobileText = mobileConfig?.text.replace('only', '');
  const showMobileBand = offerType === 'gaming';

  const titleSuffix = TITLE_SUFFIX_BY_TYPE[offerType];
  const titleEnd = titleSuffix ? ` ${titleSuffix}` : '!';

  const offerShareUrl = shareUrl ?? globalThis.window?.location.href;

  const content = (
    <>
      <div
        className={clsx(classes.banner, isDefaultIcon && classes.bannerDefaultIcon)}
        style={{ '--banner-image': `url(${finalBannerImageSrc})` } as React.CSSProperties}
        data-testid="offer-banner"
      />
      <div className={classes.content}>
        <Heading level={3} className={classes.title} skipTranslation>
          {offerType === 'cashback' ? (
            <Txt component="span">
              Earn {{ title }} {{ titleEnd }}
            </Txt>
          ) : (
            <Txt component="span" skipTranslation>
              {title}
            </Txt>
          )}
        </Heading>
        {description && (
          <Txt className={classes.description} asMarkdown skipTranslation>
            {description}
          </Txt>
        )}
        {showMobileBand && (
          <div className={classes.band}>
            <Txt>{mobileText}</Txt>
          </div>
        )}
      </div>
    </>
  );

  return (
    <Card className={clsx(classes.root, className)} data-testid="offer-banner-card">
      {content}
      {showShareOptions && (
        <Button
          className={classes.shareButton}
          onClick={() => setIsShareModalOpen(true)}
          buttonType="text"
        >
          <span className={classes.shareText}>Share</span>
          <MdShare />
        </Button>
      )}
      <SharePopover
        isOpen={isShareModalOpen}
        onClose={() => setIsShareModalOpen(false)}
        type="noTemplate"
        copyUrlOnly
        shareInfo={{
          title: title,
          description: description ?? 'Check out this amazing offer!',
          url: offerShareUrl ?? '',
          imageUrl: finalBannerImageSrc,
        }}
      />
    </Card>
  );
};

export default React.memo(OfferBanner);
